
import { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { ArrowRight, History as HistoryIcon, Brain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface SessionHistory {
  id: string;
  title: string;
  date: string;
  type: 'voice' | 'test';
  test_type?: string;
}

const History = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [sessions, setSessions] = useState<SessionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching history...');
        
        // Get voice conversations from localStorage
        const voiceSessions: SessionHistory[] = [];
        try {
          const savedHistory = localStorage.getItem('voiceConversationHistory');
          console.log('📱 Local storage data:', savedHistory);
          
          if (savedHistory) {
            const parsed = JSON.parse(savedHistory);
            if (Array.isArray(parsed)) {
              parsed.forEach((conv: any) => {
                try {
                  voiceSessions.push({
                    id: conv.id || `local-${Date.now()}-${Math.random()}`,
                    title: conv.title || 'Conversa de Voz',
                    date: conv.updatedAt || conv.createdAt || new Date().toISOString(),
                    type: 'voice' as const
                  });
                } catch (e) {
                  console.warn('Erro ao processar conversa local:', e);
                }
              });
            }
          }
        } catch (e) {
          console.error('Error loading voice conversation history:', e);
        }
        
        console.log('🎵 Voice sessions found:', voiceSessions.length);
        
        // Fetch chat sessions from Supabase if user is logged in
        let chatSessions: SessionHistory[] = [];
        if (user) {
          try {
            const { data: chatData, error: chatError } = await supabase
              .from('chat_sessions')
              .select('id, title, created_at')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false });
              
            if (chatError) {
              console.error('Error fetching chat sessions:', chatError);
            } else if (chatData) {
              chatSessions = chatData.map(session => ({
                id: session.id,
                title: session.title || 'Sessão de conversa',
                date: session.created_at,
                type: 'voice' as const
              }));
            }
          } catch (error) {
            console.error('Error fetching chat sessions:', error);
          }
        }
        
        console.log('💬 Chat sessions found:', chatSessions.length);
        
        // Combine all sessions and sort by date
        const allSessions = [...voiceSessions, ...chatSessions].sort(
          (a, b) => {
            try {
              return new Date(b.date).getTime() - new Date(a.date).getTime();
            } catch (e) {
              console.warn('Error sorting sessions by date:', e);
              return 0;
            }
          }
        );
        
        console.log('📋 Total sessions:', allSessions.length);
        setSessions(allSessions);
      } catch (error) {
        console.error('Error fetching history:', error);
        setSessions([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [user]);
  
  const filteredSessions = activeTab === 'all' 
    ? sessions 
    : sessions.filter(session => session.type === activeTab);
  
  return (
    <MainLayout>
      <div className="container mx-auto p-6">
        <h1 className="text-2xl font-bold mb-2">Histórico de Sessões</h1>
        <p className="text-sm text-gray-500 mb-6">Acompanhe suas sessões e evolução</p>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="voice">Conversas</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="space-y-6">
          <Card className="bg-blue-50 p-6 border-blue-100 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="mb-4 sm:mb-0">
                <div className="flex items-center gap-2">
                  <HistoryIcon className="h-5 w-5 text-brand-blue" />
                  <h3 className="font-medium text-lg">Histórico de Sessões</h3>
                </div>
                <p className="text-sm text-gray-600 mt-1">Acesse o histórico completo de suas sessões e conversas</p>
              </div>
              <div className="flex gap-3">
                <Button asChild variant="outline">
                  <Link to="/historico-testes">
                    <Brain className="h-4 w-4 mr-2" />
                    Ver testes
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/agente-voz">
                    Nova conversa
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Carregando seu histórico...</p>
            </div>
          ) : filteredSessions.length > 0 ? (
            <div className="space-y-4">
              {filteredSessions.map(session => (
                <Card key={session.id} className="flex items-center justify-between p-6 hover:shadow transition-all">
                  <div>
                    <h3 className="text-lg font-medium">{session.title}</h3>
                    <span className="text-sm text-gray-500">
                      {(() => {
                        try {
                          return format(new Date(session.date), "d 'de' MMMM, yyyy 'às' HH:mm", { locale: ptBR });
                        } catch (e) {
                          return 'Data inválida';
                        }
                      })()}
                    </span>
                    <div className="mt-1">
                      <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {session.type === 'voice' ? 'Conversa de Voz' : 'Teste'}
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" asChild>
                    <Link to={session.type === 'voice' ? `/agente-voz` : `/sessao/${session.id}`}>
                      <ArrowRight className="text-brand-blue" />
                    </Link>
                  </Button>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500 mb-4">Você ainda não tem sessões anteriores</p>
              <Button asChild>
                <Link to="/agente-voz">
                  Iniciar primeira sessão
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default History;
